import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../../staff/StaffSideMenu";
import API_URL from "../../../api/API";
import { useStaffAuthContext } from "../../staff/useStaffAuthContext";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { CiSearch } from "react-icons/ci";
import { RxCross2 } from "react-icons/rx";
import { FiEdit, FiEdit2 } from "react-icons/fi";
import { RiDeleteBin4Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";

export const AdminManageStrengthTrainingProducts = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const [newTitle, setNewTitle] = useState("");
  const [newInfo, setNewInfo] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newImage, setNewImage] = useState("");
  const [newYoutubeUrl, setNewYoutubeUrl] = useState(""); // ✅ NEW FIELD

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const [newStatus, setNewStatus] = useState("DRAFT"); // default DRAFT

  useEffect(() => {
    if (staff?.token) fetchProducts();
  }, [staff?.token, currentPage]);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(API_URL.ADMIN_MANAGE_STRENGTH_PRODUCT, {
        headers: { Authorization: `Bearer ${staff.token}` },
      });
      const data = await res.json();
      setProducts(data.data || []);
      setTotalPages(Math.ceil((data.data || []).length / itemsPerPage));
    } catch (err) {
      setError("Failed to load products.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    try {
      const formData = new FormData();
      formData.append("title", newTitle);
      formData.append("info", newInfo);
      formData.append("price", newPrice);
      formData.append("youtube_url", newYoutubeUrl); // ✅ Add YouTube URL
      if (newImage) {
        formData.append("image", newImage); // file object
      }

      const res = await fetch(API_URL.ADMIN_CREATE_STRENGTH_PRODUCT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${staff.token}`,
          // NOTE: Do NOT set Content-Type header here! Let browser set it with multipart boundary
        },
        body: formData,
      });

      if (!res.ok) throw new Error("Failed to create product.");

      const newData = await res.json();
      alert("New Strength Training Product Created");
      setNewTitle("");
      setNewInfo("");
      setNewPrice("");
      setNewImage("");
      setNewYoutubeUrl("");
      setCreateModalOpen(false);
      fetchProducts();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleSearch = (e) => setSearchQuery(e.target.value.toLowerCase());
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const filteredProducts = products.filter((item) =>
    item.title?.toLowerCase().includes(searchQuery),
  );

  const handleDeleteProduct = async () => {
    try {
      const res = await fetch(
        `${API_URL.ADMIN_DELETE_STRENGTH_PRODUCT}${selectedProductId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );
      if (!res.ok) throw new Error("Failed to delete product.");
      setDeleteModalOpen(false);
      setSelectedProductId(null);
      alert("Product deleted successfully");
      fetchProducts();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    try {
      const formData = new FormData();

      // Append text fields
      formData.append("title", editingProduct.title || "");
      formData.append("info", editingProduct.info || "");
      formData.append("price", editingProduct.price || "");
      formData.append("status", editingProduct.status || "DRAFT"); // ✅ Added
      formData.append("youtube_url", editingProduct.youtube_url || ""); // ✅ Include YouTube URL

      // Only append image if a new one is selected
      if (editingProduct.imageFile instanceof File) {
        formData.append("image", editingProduct.imageFile);
      }

      const response = await fetch(
        `${API_URL.ADMIN_EDIT_STRENGTH_PRODUCT}${editingProduct._id}`,
        {
          method: "POST", // or "POST" depending on your backend
          headers: { Authorization: `Bearer ${staff.token}` },
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update product");
      }

      const updatedProduct = await response.json();

      fetchProducts();

      alert("Product updated successfully");
      setEditModalOpen(false);
    } catch (error) {
      console.error("Update failed:", error);
      alert("Failed to update product");
    }
  };

  return (
    <div className="flex md:h-screen font-inter">
      <StaffSideMenu />
      <div className="max-md:w-full md:flex-1 bg-bgBase md:flex md:flex-col md:overflow-hidden md:overflow-y-auto">
        {/* --- Top Bar --- */}
        <div className="bg-white w-full pt-5 pb-3 md:py-6 px-5 md:px-10">
          <div className="w-full max-w-screen-2xl">
            <div className="flex max-lg:flex-col justify-between lg:items-center gap-5">
              <h1 className="font-inter font-medium text-dark text-xl md:text-2xl xl:text-[28px] !leading-none max-md:pl-12">
                Products
              </h1>
              <div className="flex max-md:flex-col md:items-center gap-3">
                <div className="relative w-full min-w-64 max-w-64">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search products..."
                    className="w-full p-2.5 border border-black/10 rounded-lg font-inter font-normal text-sm leading-none pl-10"
                  />
                  <span className="absolute top-1/2 -translate-y-1/2 left-3">
                    <CiSearch color="#666666" size={20} />
                  </span>
                </div>
                <button
                  onClick={() => setCreateModalOpen(true)}
                  className="px-6 py-2.5 max-md:w-fit bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black"
                >
                  Create Product
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* --- Product Table --- */}
        <div className="py-5 md:py-8 px-5 md:px-10">
          <div className="w-full max-w-screen-2xl">
            {error && (
              <div className="bg-red-100 text-red-500 p-3 rounded text-sm font-normal mb-4">
                {error}
              </div>
            )}

            {isLoading ? (
              <div className="bg-white py-2 px-10 rounded-md text-dark font-normal">
                Loading...
              </div>
            ) : (
              <>
                <div className="overflow-auto show-scrollbar md:max-h-[600px]">
                  <div className="rounded-10 border border-tblBorder w-full">
                    <table className="table-auto table-radius w-full min-w-[680px] md:min-w-[720px]">
                      <thead className="sticky top-0 bg-tblHead text-slate100 text-xs font-medium">
                        <tr>
                          <th className="px-6 py-3 text-left">
                            <p>Image</p>
                          </th>
                          <th className="px-6 py-3 text-left">
                            <p>Title</p>
                          </th>
                          <th className="px-6 py-3 text-left">
                            <p>Info</p>
                          </th>
                          <th className="px-6 py-3 text-left">
                            <p>Price</p>
                          </th>
                          <th className="px-6 py-3 text-left">
                            <p>YouTube</p>
                          </th>
                          <th className="px-6 py-3 text-left">
                            <p>Status</p>
                          </th>
                          <th className="px-6 py-3 text-left">
                            <p>Actions</p>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-[#E5E5E5]">
                        {filteredProducts
                          .slice(
                            (currentPage - 1) * itemsPerPage,
                            currentPage * itemsPerPage,
                          )
                          .map((item, index) => (
                            <tr key={item._id} className={``}>
                              <td className="px-3 md:px-6 py-2 md:py-4">
                                {item.image ? (
                                  <img
                                    src={API_URL.SERVER_PICTURE_URL + item.image}
                                    alt={item.title}
                                    className="h-12 w-12 object-cover rounded-lg border border-tblBorder"
                                  />
                                ) : (
                                  <>
                                    <p className="font-normal">No Image</p>
                                  </>
                                )}
                              </td>
                              <td className="px-3 md:px-6 py-2 md:py-4">
                                <p className="text-dark font-medium text-sm">
                                  {item.title}
                                </p>
                              </td>
                              <td className="px-3 md:px-6 py-2 md:py-4">
                                <p className="text-dark font-medium text-sm line-clamp-1">
                                  {item.info}
                                </p>
                              </td>
                              <td className="px-3 md:px-6 py-2 md:py-4">
                                <p className="text-dark font-normal text-sm">
                                  ${item.price}
                                </p>
                              </td>
                              <td className="px-3 md:px-6 py-2 md:py-4">
                                {item.youtube_url ? (
                                  <a
                                    href={item.youtube_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-dark font-normal text-sm underline"
                                  >
                                    View
                                  </a>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="px-3 md:px-6 py-2 md:py-4">
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                    item.status === "PUBLISHED"
                                      ? "bg-green-100 text-[#2E7D32]"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {item.status || "DRAFT"}
                                </span>
                              </td>
                              <td className="px-3 md:px-6 py-2 md:py-4">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      setEditingProduct(item);
                                      setEditModalOpen(true);
                                    }}
                                    className="p-2 rounded-lg hover:bg-gray-200"
                                  >
                                    <FiEdit color="#666666" size={18} />
                                    {/* <FaRegEdit /> */}
                                  </button>

                                  <button
                                    onClick={() => {
                                      setSelectedProductId(item._id);
                                      setDeleteModalOpen(true);
                                    }}
                                    className="p-2 rounded-lg hover:bg-gray-200"
                                  >
                                    <RiDeleteBin4Line color="#666666" size={18} />
                                  </button>

                                  <Link
                                    to={`/administrator/template-strength/workout-builder/${item._id}`}
                                    className="px-3 py-2 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black"
                                  >
                                    Build
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
          <div className="bg-white block md:mt-auto">
            <div className="w-full max-w-screen-2xl flex justify-between items-center p-4">
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-xs sm:text-sm ${
                  currentPage === 1 ? "cursor-not-allowed" : "text-black"
                }`}
              >
                <MdKeyboardArrowLeft size={20} /> Previous
              </button>
              <span className="text-dark font-inter font-normal text-xs sm:text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-xs sm:text-sm ${
                  currentPage === totalPages
                    ? "cursor-not-allowed"
                    : "text-black"
                }`}
              >
                Next <MdKeyboardArrowRight size={20} />
              </button>
            </div>
          </div>

        {/* Delete Modal */}
        {deleteModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 p-5 overflow-y-auto flex justify-center z-50">
            <div className="w-full max-w-xl max-h-[99vh] overflow-y-auto">
              <div className="bg-white rounded-lg w-full shadow-lg">
                <div className="relative py-5 pl-5 md:pl-6 pr-14">
                  <h3 className="text-dark text-xl font-medium capitalize">
                    Confirm Deletion
                  </h3>
                  <div className="absolute top-4 right-6">
                    <button
                      onClick={() => setDeleteModalOpen(false)}
                      className="p-2 rounded hover:bg-gray-200 flex justify-center items-center"
                    >
                      <RxCross2 color="#4A5565" size={18} />
                    </button>
                  </div>
                </div>
                <div className="p-5 md:p-6 space-y-5 border-y border-tblBorder">
                  <p className="text-sm font-normal text-dark">
                    Are you sure you want to delete this product?
                  </p>
                </div>
                <div className="p-5 md:p-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setDeleteModalOpen(false)}
                    className="px-5 py-2.5 flex items-center gap-2 bg-white hover:bg-tblHead border border-tblBorder
                      text-dark font-inter font-medium text-sm leading-tight rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteProduct}
                    className="px-5 py-2.5 bg-dark text-white font-inter font-medium text-sm leading-tight rounded-lg hover:bg-black"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Modal */}
        {createModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 p-5 overflow-y-auto flex justify-center z-50">
            <div className="w-full max-w-xl max-h-[99vh] overflow-y-auto">
              <div className="bg-white rounded-lg w-full shadow-lg">
                {/* title / cancel */}
                <div className="relative py-5 pl-5 md:pl-6 pr-14">
                  <h2 className="text-dark text-xl font-medium capitalize">
                    Create Strength Training Product
                  </h2>
                  <div className="absolute top-4 right-6">
                    <button
                      onClick={() => setCreateModalOpen(false)}
                      className="p-2 rounded hover:bg-gray-200 flex justify-center items-center"
                    >
                      <RxCross2 color="#4A5565" size={18} />
                    </button>
                  </div>
                </div>
                {/* inputs */}
                <div className="p-5 md:p-6 space-y-5 border-y border-tblBorder">
                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      Info
                    </label>
                    <textarea
                      value={newInfo}
                      onChange={(e) => setNewInfo(e.target.value)}
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      Price
                    </label>
                    <input
                      type="text"
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      YouTube Url
                    </label>
                    {/* ✅ YouTube URL */}
                    <input
                      value={newYoutubeUrl}
                      onChange={(e) => setNewYoutubeUrl(e.target.value)}
                      placeholder="YouTube URL (optional)"
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      Upload Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setNewImage(e.target.files[0]); // store the File object
                        }
                      }}
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                    />
                    {newImage && typeof newImage === "object" && (
                      <p className="mt-1 text-sm text-slate100 font-medium">
                        {newImage.name}
                      </p>
                    )}
                  </div>
                </div>

                <div className="p-5 md:p-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setCreateModalOpen(false)}
                    className="px-5 py-2.5 flex items-center gap-2 bg-white hover:bg-tblHead border border-tblBorder
                      text-dark font-inter font-medium text-sm leading-tight rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateProduct}
                    className="px-5 py-2.5 bg-dark text-white font-inter font-medium text-sm leading-tight rounded-lg hover:bg-black"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {editModalOpen && editingProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-60 p-5 overflow-y-auto flex justify-center z-50">
            <div className="w-full max-w-xl max-h-[99vh] overflow-y-auto">
              <div className="bg-white rounded-lg w-full shadow-lg">
                {/* title */}
                <div className="relative py-5 pl-6 pr-14">
                  <h2 className="text-dark text-xl font-medium capitalize">
                    Edit Product
                  </h2>
                  <div className="absolute top-4 right-6">
                    <button
                      onClick={() => setEditModalOpen(false)}
                      className="p-2 rounded hover:bg-gray-200 flex justify-center items-center"
                    >
                      <RxCross2 color="#4A5565" size={18} />
                    </button>
                  </div>
                </div>
                {/* inputs */}
                <div className="p-5 md:p-6 space-y-5 border-y border-tblBorder">
                  {/* Title */}
                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={editingProduct.title}
                      onChange={(e) =>
                        setEditingProduct({
                          ...editingProduct,
                          title: e.target.value,
                        })
                      }
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                    />
                  </div>

                  {/* Info */}
                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      Info
                    </label>
                    <textarea
                      value={editingProduct.info}
                      onChange={(e) =>
                        setEditingProduct({
                          ...editingProduct,
                          info: e.target.value,
                        })
                      }
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                      rows={2}
                    />
                  </div>

                  {/* Price */}
                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      Price
                    </label>
                    <input
                      type="text"
                      value={editingProduct.price}
                      onChange={(e) =>
                        setEditingProduct({
                          ...editingProduct,
                          price: e.target.value,
                        })
                      }
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      YouTube
                    </label>
                    {/* ✅ YouTube URL Field */}
                    <input
                      value={editingProduct.youtube_url || ""}
                      onChange={(e) =>
                        setEditingProduct({
                          ...editingProduct,
                          youtube_url: e.target.value,
                        })
                      }
                      placeholder="YouTube URL"
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                    />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      Upload Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setEditingProduct((prev) => ({
                            ...prev,
                            imageFile: file,
                          }));
                        }
                      }}
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                    />
                    {editingProduct.imageFile instanceof File ? (
                      <img
                        src={URL.createObjectURL(editingProduct.imageFile)}
                        alt="Preview"
                        className="mt-1 rounded max-h-24 object-contain border"
                      />
                    ) : editingProduct.image ? (
                      <img
                        src={API_URL.SERVER_PICTURE_URL + editingProduct.image}
                        alt="Product"
                        className="mt-1 rounded max-h-24 object-contain border"
                      />
                    ) : null}
                  </div>

                  <div>
                    <label className="block text-slate100 font-normal text-sm mb-1">
                      Status
                    </label>
                    <select
                      value={editingProduct.status || "DRAFT"}
                      onChange={(e) =>
                        setEditingProduct({
                          ...editingProduct,
                          status: e.target.value,
                        })
                      }
                      className="w-full border border-tblBorder font-normal text-sm text-dark py-2.5 px-3 rounded-md outline-none"
                    >
                      <option value="DRAFT">DRAFT</option>
                      <option value="PUBLISHED">PUBLISHED</option>
                    </select>
                  </div>
                </div>
                {/* Actions */}
                <div className="p-5 md:p-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setEditModalOpen(false)}
                    className="px-5 py-2.5 flex items-center gap-2 bg-white hover:bg-tblHead border border-tblBorder
                      text-dark font-inter font-medium text-sm leading-tight rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateProduct}
                    className="px-5 py-2.5 bg-dark text-white font-inter font-medium text-sm leading-tight rounded-lg hover:bg-black ml-auto"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
